// import axios from './AxiosService';
import axios from 'axios'; // use default axios
import { IAttachedJob, IQuestionnaire, IQuestionnaireJobLink } from '../types/questionnaire';
import { getDecodedToken, getTokenFromCookie } from '../utils/tokenHelper';
// Base URL for API requests to our local server
// const API_BASE_URL = 'http://localhost:3000/api/v1';
const API_BASE_URL = '/api/v1';

//For local testing
// const API_BASE_URL = 'https://qa-formbuilder-api.jobtarget.com/api/v1';

export const FormBuilderService = {
  // get existing questionnaire
  getQuestionnaire: async (questionnaireId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.get<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // create new questionnaire
  createQuestionnaire: async (questionnaire: IQuestionnaire) => {
    const payload = FormBuilderService.normalizeQuestionnaire(questionnaire, false);
    const decodedToken = getDecodedToken();
    const token = getTokenFromCookie();

    //fix: can handle dynamic companyId and userId from jtToken
    payload.companyId = decodedToken?.companyId;
    payload.userId = decodedToken?.userId;
    const response = await axios.post<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire`,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // update questionnaire
  updateQuestionnaire: async (questionnaireId: string, questionnaire: IQuestionnaire) => {
    const token = getTokenFromCookie();
    const payload = FormBuilderService.normalizeQuestionnaire(questionnaire, true);
    const response = await axios.put<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}`,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // get all questionnaires by company id
  getQuestionnairesByCompanyId: async (companyId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.get<IQuestionnaire[]>(
      `${API_BASE_URL}/company/${companyId}/questionnaire`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // delete questionnaire (Change to active status)
  deactivateQuestionnaireStatus: async (questionnaireId: string, active: boolean, companyId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.delete<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}`,
      {
        data: {
          active,
          companyId
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // get active jobs count
  getActiveJobsCount: async (questionnaireId: string) => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get<number>(
        `${API_BASE_URL}/questionnaire/${questionnaireId}/job/count`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error: any) {

      if (error.response?.status === 404) {
        return 0; // Return 0 for not found questionnaires
      }
      return 0;
    }
  },

  // VI-612: total completed screener submissions for a questionnaire (across all its jobs)
  getCompletedSubmissionCount: async (questionnaireId: string) => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get<number>(
        `${API_BASE_URL}/questionnaire/${questionnaireId}/interviews/count`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return 0; // Return 0 for not found questionnaires
      }
      return 0;
    }
  },

  // VI-278: get active jobs attached to a questionnaire with submission counts
  getAttachedJobsWithSubmissions: async (questionnaireId: string): Promise<IAttachedJob[]> => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get<IAttachedJob[]>(
        `${API_BASE_URL}/questionnaire/${questionnaireId}/jobs`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  // patch questionnaire (e.g. active)
  patchQuestionnaire: async (questionnaireId: string, data: any) => {
    const token = getTokenFromCookie();
    const response = await axios.patch<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // associate questionnaire with a job
  associateQuestionnaireWithJob: async (jobId: number, questionnaireId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.post(
      `${API_BASE_URL}/job/${jobId}/questionnaire`,
      JSON.stringify({
        questionnaireId: questionnaireId
      }),
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // get all questionnaire job links (active + soft-deleted) for a job
  // returns the full history of questionnaires that have been attached to the job
  getAllQuestionnaireLinksByJobId: async (jobId: number): Promise<IQuestionnaireJobLink[]> => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get<IQuestionnaireJobLink[]>(
        `${API_BASE_URL}/job/${jobId}/questionnaires`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data || [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error(`Error fetching questionnaire link history for job ${jobId}:`, error);
      return [];
    }
  },

  // check if a job has an associated questionnaire
  checkJobQuestionnaire: async (jobId: number) => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get(
        `${API_BASE_URL}/job/${jobId}/questionnaire`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 204) {
        return { hasQuestionnaire: false, data: null };
      }
      // If we get a 200 response, it means there is a questionnaire associated
      return { hasQuestionnaire: true, data: response.data };
    } catch (error) {
      // If we get an error, it means there is no questionnaire associated
      // We specifically return false rather than re-throwing the error
      return { hasQuestionnaire: false, data: null };
    }
  },

  // batch check if multiple jobs have associated questionnaires
  batchCheckJobQuestionnaires: async (jobIds: number[]) => {
    // If there's no backend batch endpoint, we'll implement it client-side
    // by limiting concurrent requests
    const BATCH_SIZE = 5;
    const results: Array<{ jobId: number, hasQuestionnaire: boolean, data: any }> = [];

    // Process job IDs in batches to avoid overwhelming the server
    for (let i = 0; i < jobIds.length; i += BATCH_SIZE) {
      const batchJobIds = jobIds.slice(i, i + BATCH_SIZE);

      // Create a promise for each job ID in the current batch
      const batchPromises = batchJobIds.map(async (jobId) => {
        try {
          const result = await FormBuilderService.checkJobQuestionnaire(jobId);
          return {
            jobId,
            hasQuestionnaire: result.hasQuestionnaire,
            data: result.data
          };
        } catch (error) {
          console.error(`Error checking questionnaire for job ${jobId}:`, error);
          return {
            jobId,
            hasQuestionnaire: false,
            data: null
          };
        }
      });

      // Wait for all promises in this batch to resolve
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    return results;
  },

  // detach a questionnaire from a job
  detachQuestionnaireFromJob: async (jobId: number, questionnaireId?: string) => {
    const token = getTokenFromCookie();
    // We need to get the current questionnaireId before we can detach it
    try {
      // First, check if there's a questionnaire associated 
      const questionnaireCheck = await FormBuilderService.checkJobQuestionnaire(jobId);

      if (!questionnaireCheck.hasQuestionnaire) {
        throw new Error('No questionnaire found to detach');
      }

      // Get the ID to detach - either the provided ID or from the check result
      const idToDetach = questionnaireId ||
        (questionnaireCheck.data && questionnaireCheck.data.questionnaireId);

      // Ensure we have a valid questionnaire ID to detach
      if (!idToDetach) {
        throw new Error('No questionnaire ID available for detachment');
      }

      console.log(`Detaching questionnaire ID: ${idToDetach} from job ID: ${jobId}`);

      // Make the DELETE request with the questionnaireId in the body
      const response = await axios.delete(
        `${API_BASE_URL}/job/${jobId}/questionnaire`,
        {
          data: JSON.stringify({
            questionnaireId: idToDetach
          }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error in detachQuestionnaireFromJob:", error);
      throw error;
    }
  },
  // set a questionnaire as the company default
  setDefaultQuestionnaire: async (questionnaireId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.put<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}/default`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // remove a questionnaire as the company default
  removeDefaultQuestionnaire: async (questionnaireId: string) => {
    const token = getTokenFromCookie();
    const response = await axios.delete<IQuestionnaire>(
      `${API_BASE_URL}/questionnaire/${questionnaireId}/default`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  },

  // get the default questionnaire for a company
  getDefaultQuestionnaire: async (companyId: string) => {
    try {
      const token = getTokenFromCookie();
      const response = await axios.get<IQuestionnaire>(
        `${API_BASE_URL}/company/${companyId}/questionnaire/default`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No default questionnaire set
      }
      throw error;
    }
  },

  /**
   * Helper functions
   * 
   */

  mapQuestionType: (type: string): string => {
    const typeMap: Record<string, string> = {
      text: 'Text',
      select: 'Select',
      multiselect: 'Multiselect',
    };

    return typeMap[type.toLowerCase()] || 'Text';
  },

  // convert to FormBuilder API format
  normalizeQuestionnaire: (questionnaire: IQuestionnaire, includeId: boolean = false) => {
    const payload: any = {
      questionnaireName: questionnaire.questionnaireName,
      companyId: questionnaire.companyId || 1,
      userId: questionnaire.userId || 1,
      contentType: questionnaire.contentType === 'audio' ? 'Audio' : 'Video',
      questions: questionnaire.questions.map((q, index) => ({
        questionId: q.id || `question-${index + 1}`,
        questionText: q.text,
        required: q.required,
        type: FormBuilderService.mapQuestionType(q.type),
        options: q.options || null,
      })),
      active: questionnaire.active !== undefined ? questionnaire.active : true,
    };
    // Include ID only for updates
    if (includeId && questionnaire.id) {
      payload.id = questionnaire.id;
    }
    return payload;
  },
};
